import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Camera, Upload, X, Image as ImageIcon } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const ImageUpload = ({ onImageSelect, maxSize = 5, multiple = false }) => {
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [captureMode, setCaptureMode] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const validFiles = [];
    const newPreviews = [];

    for (const file of files) {
      // Validate file size (in MB)
      if (file.size > maxSize * 1024 * 1024) {
        toast({
          title: 'File Too Large',
          description: `${file.name} is larger than ${maxSize}MB`,
          variant: 'destructive'
        });
        continue;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Invalid File',
          description: `${file.name} is not an image file`,
          variant: 'destructive'
        });
        continue;
      }

      validFiles.push(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push({ file, preview: e.target.result });
        if (newPreviews.length === validFiles.length) {
          setPreviews(multiple ? [...previews, ...newPreviews] : newPreviews);
          onImageSelect(multiple ? validFiles : validFiles[0]);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } // Use back camera on mobile
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        setStream(mediaStream);
        setCaptureMode(true);
      }
    } catch (error) {
      console.error('Camera access error:', error);
      toast({
        title: 'Camera Access Denied',
        description: 'Please allow camera access to take photos',
        variant: 'destructive'
      });
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCaptureMode(false);
  };

  const capturePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      
      canvas.toBlob((blob) => {
        const file = new File([blob], `capture_${Date.now()}.jpg`, { type: 'image/jpeg' });
        
        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          const newPreview = { file, preview: e.target.result };
          setPreviews(multiple ? [...previews, newPreview] : [newPreview]);
        };
        reader.readAsDataURL(file);
        
        // Pass file to parent
        onImageSelect(file);
        
        // Stop camera
        stopCamera();
      }, 'image/jpeg', 0.9);
    }
  };

  const removeImage = (index) => {
    const newPreviews = previews.filter((_, i) => i !== index);
    setPreviews(newPreviews);
    
    if (newPreviews.length === 0) {
      onImageSelect(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } else {
      onImageSelect(multiple ? newPreviews.map(p => p.file) : newPreviews[0]?.file);
    }
  };

  const clearAllImages = () => {
    setPreviews([]);
    onImageSelect(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-4">
      {!captureMode && previews.length === 0 && (
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            className="flex-1"
          >
            <Upload className="w-4 h-4 mr-2" />
            {multiple ? 'Upload Images' : 'Upload from Device'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={startCamera}
            className="flex-1"
          >
            <Camera className="w-4 h-4 mr-2" />
            Take Photo
          </Button>
        </div>
      )}

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Camera View */}
      {captureMode && (
        <div className="space-y-3">
          <div className="relative bg-black rounded-lg overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full h-64 object-cover"
            />
          </div>
          <div className="flex gap-3">
            <Button
              type="button"
              onClick={capturePhoto}
              className="flex-1"
            >
              <Camera className="w-4 h-4 mr-2" />
              Capture Photo
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={stopCamera}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Image Previews */}
      {previews.length > 0 && !captureMode && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {previews.map((item, index) => (
              <div key={index} className="relative">
                <img
                  src={item.preview}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-40 object-cover rounded-lg border"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => removeImage(index)}
                  className="absolute top-2 right-2"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
          
          <div className="flex items-center justify-between">
            <p className="text-xs text-green-600 flex items-center">
              <ImageIcon className="w-4 h-4 mr-1" />
              {previews.length} image{previews.length > 1 ? 's' : ''} ready to upload
            </p>
            {multiple && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-4 h-4 mr-2" />
                Add More
              </Button>
            )}
          </div>
          
          {previews.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={clearAllImages}
              className="w-full"
            >
              Clear All
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
